import React, { useState, useEffect, useContext, useRef } from 'react';
import '../GridEditor/GridEditor.css';
import MatchupDisplay from '../GridEditor/MatchupDisplay';
import { ItemBuildProgressionV2, ChampionBuildData, BuildOrderUpdateRequest, BuildOrderUpdateResponse } from './ItemBuild';
import { GoldControlsV2, GoldEventsData } from './GoldControls';
import { WaveProgressionTableV3, WaveData, WaveSelectionManager } from './WaveTable';
import GameAssetsContext from '../../../XXX/context/GameAssetContext';
import { getWaveConfig } from '../../../utils/waveConfig';
import WaveExportV4 from './WaveExport/WaveExportV4';

// Champion interface for matchup display
interface Champion {
  id: string;
  name: string;
  level: number;
  teamColor: 'blue' | 'red';
}

// Map DB lane names (MIDDLE, BOTTOM, etc.) to Role type
const LANE_TO_ROLE: Record<string, 'top' | 'jgl' | 'mid' | 'bot' | 'sup'> = {
  TOP: 'top',
  JUNGLE: 'jgl',
  MIDDLE: 'mid',
  BOTTOM: 'bot',
  UTILITY: 'sup',
};

interface Props {
  championName: string;
  opponentName?: string;
  defaultLane?: string;
  className?: string;
  // Callbacks for backend API integration
  onBuildOrderChange?: (championName: string, buildOrder: { starter?: string; mainItems: string[] }) => void;
  onWaveExport?: (selectedWaves: number[], championName: string) => void;
  onWaveExportResult?: (result: any, error: string | null) => void;
  loadedWaves?: number[]; // Currently loaded waves in the analysis grid
  analysisGridResponse?: any; // WaveAnalysisResponse for group metadata
  onGoldEventsChange?: (championName: string, goldEvents: any) => void;
}

const StaticGridEditor: React.FC<Props> = ({
  championName,
  opponentName,
  defaultLane,
  className = '',
  onBuildOrderChange,
  onWaveExport,
  onWaveExportResult,
  loadedWaves = [],
  analysisGridResponse,
  onGoldEventsChange
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  
  // Component instance tracking
  const componentId = React.useRef(Math.random().toString(36).substr(2, 8));
  // AbortController for cancelling in-flight API requests on champion change
  const abortControllerRef = useRef<AbortController | null>(null);
  console.log(`[StaticGridEditor] 🏗️ Component instance ${componentId.current} initialized/re-rendered`);

  // State for champion build data (will be loaded from backend)
  const [primaryChampionBuild, setPrimaryChampionBuild] = useState<ChampionBuildData | null>(null);
  const [opponentChampionBuild, setOpponentChampionBuild] = useState<ChampionBuildData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Gold events state (CSPM initialized after initialRole is available below)
  
  const [activeChampion, setActiveChampion] = useState<'primary' | 'opponent'>('primary');
  
  // Wave progression state
  const [primaryWaveProgression, setPrimaryWaveProgression] = useState<WaveData[]>([]);
  const [opponentWaveProgression, setOpponentWaveProgression] = useState<WaveData[]>([]);
  const [waveProgressionLoading, setWaveProgressionLoading] = useState(false);
  const [waveProgressionError, setWaveProgressionError] = useState<string | null>(null);
  
  // Backend-provided effective build orders (source of truth for display)
  const [effectiveBuildOrders, setEffectiveBuildOrders] = useState<Record<string, { starter?: string; mainItems: string[] }>>({});
  
  // Log component initialization
  console.log(`[StaticGridEditor] 🎯 Component initialized with:`, {
    championName: `"${championName}"`,
    opponentName: `"${opponentName}"`,
    primaryBuild: primaryChampionBuild?.championName ? `"${primaryChampionBuild.championName}"` : 'null',
    opponentBuild: opponentChampionBuild?.championName ? `"${opponentChampionBuild.championName}"` : 'null'
  });
  
  // Active wave champion (separate from gold controls)
  const [activeWaveChampion, setActiveWaveChampion] = useState<'primary' | 'opponent'>('primary');
  
  // Wave selection state
  const [selectedWaves, setSelectedWaves] = useState<number[]>([]);
  
  // Collapsible section state
  const [isCustomizeCollapsed, setIsCustomizeCollapsed] = useState(true);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);
  const [isGoldControlsCollapsed, setIsGoldControlsCollapsed] = useState(false);
  const [isWaveTableCollapsed, setIsWaveTableCollapsed] = useState(false);
  const [isWaveExportCollapsed, setIsWaveExportCollapsed] = useState(false);
  
  // Role quest state
  type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';
  const initialRole = (defaultLane && LANE_TO_ROLE[defaultLane]) || 'mid';
  const [primaryRole, setPrimaryRole] = useState<Role>(initialRole);
  const [opponentRole, setOpponentRole] = useState<Role>(initialRole);
  const [primaryQuestWave, setPrimaryQuestWave] = useState<number | null>(null);
  const [opponentQuestWave, setOpponentQuestWave] = useState<number | null>(null);

  // Default CSPM: 0 for support (no CS), 10 for all other roles
  const getDefaultCSPM = (role: string) => role === 'sup' ? 0 : 10;
  const getDefaultSupportProcs = (role: string) => role === 'sup' ? 3 : undefined;

  // Gold events state
  const [primaryGoldEvents, setPrimaryGoldEvents] = useState<GoldEventsData>({
    cspm: getDefaultCSPM(initialRole),
    cspmRanges: [],
    kills: [],
    turrets: [],
    plates: [],
    miscellaneous: [],
    supportProcsPerMin: getDefaultSupportProcs(initialRole)
  });
  
  const [opponentGoldEvents, setOpponentGoldEvents] = useState<GoldEventsData>({
    cspm: getDefaultCSPM(initialRole),
    cspmRanges: [],
    kills: [],
    turrets: [],
    plates: [],
    miscellaneous: [],
    supportProcsPerMin: getDefaultSupportProcs(initialRole)
  });

  // Sync role when defaultLane prop arrives from API and re-trigger build load
  useEffect(() => {
    if (defaultLane && LANE_TO_ROLE[defaultLane]) {
      const newRole = LANE_TO_ROLE[defaultLane];
      setPrimaryRole(newRole);
      setOpponentRole(newRole);

      // Update CSPM + support procs defaults when role changes
      const newDefaultCSPM = getDefaultCSPM(newRole);
      const newSupportProcs = getDefaultSupportProcs(newRole);
      setPrimaryGoldEvents(prev => ({ ...prev, cspm: newDefaultCSPM, supportProcsPerMin: newSupportProcs }));
      setOpponentGoldEvents(prev => ({ ...prev, cspm: newDefaultCSPM, supportProcsPerMin: newSupportProcs }));

      // Re-send build to backend with the correct role if we already have build data
      if (primaryChampionBuild) {
        const buildOrder = buildFullItemOrder(primaryChampionBuild.itemBuild, newRole);
        const goldEvents: GoldEventsData = {
          cspm: newDefaultCSPM, cspmRanges: [], kills: [], turrets: [], plates: [], miscellaneous: [],
          supportProcsPerMin: newSupportProcs
        };
        updateGoldEventsInBackendWithBuildOrder(
          championName, goldEvents, buildOrder,
          undefined, opponentName || 'Garen', true, newRole, newRole
        ).then(result => {
          if (result.success && result.data) {
            if (result.data.waveProgression) setPrimaryWaveProgression(result.data.waveProgression);
            if (result.data.opponentWaves) setOpponentWaveProgression(result.data.opponentWaves);
            if (result.data.championGrid && onWaveExportResult) {
              onWaveExportResult({ success: true, data: result.data.championGrid }, null);
            }
          }
        }).catch(err => console.error('[StaticGridEditor] Error re-sending with new role:', err));
      }
    }
  }, [defaultLane]);
  
  // Custom build order state (tracks user customizations from ItemShop)
  const [primaryCustomBuildOrder, setPrimaryCustomBuildOrder] = useState<{ starter?: string; mainItems: string[] } | null>(null);
  const [opponentCustomBuildOrder, setOpponentCustomBuildOrder] = useState<{ starter?: string; mainItems: string[] } | null>(null);
  
  // Request deduplication
  const [isLoadingBuilds, setIsLoadingBuilds] = useState(false);
  
  // Boot item IDs for filtering duplicates
  const BOOTS_IDS = new Set([
    '1001', // Basic Boots
    '3006', '3009', '3020', '3047', '3111', '3117', '3158', // T1 boots
    '223009', '223020', '223047', '223111', '223158', '223006' // T2 boots
  ]);
  
  const isBootsItem = (itemId: string | number): boolean => BOOTS_IDS.has(String(itemId));
  
  // Support item constants
  const FINAL_SUPPORT_ITEM_IDS = new Set(['3869', '3870', '3871', '3876', '3877']); // Celestial Opposition, Dream Maker, Zaz'Zak's, Solstice Sleigh, Bloodsong
  const ALL_SUPPORT_ITEM_IDS = new Set([
    '3865', '3866', '3867', // World Atlas, Runic Compass, Bounty of Worlds
    '3869', '3870', '3871', '3876', '3877' // Final support items
  ]);

  // Jungle starter item constants
  const JUNGLE_STARTER_IDS = new Set(['1101', '1102', '1103']); // Scorchclaw Pup, Gustwalker Hatchling, Mosstomper Seedling
  const DEFAULT_JUNGLE_STARTER = '1101'; // Scorchclaw Pup (red)
  const DEFAULT_NON_JUNGLE_STARTER = '1055'; // Doran's Blade

  // Helper: get itemGroups for an item ID from gameWideAssets
  const getItemGroupsById = (itemId: string): string[] => {
    const item = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
    return (item as any)?.itemGroups || [];
  };

  // Helper: check if adding an item would cause a group conflict
  const hasGroupConflict = (itemId: string, ownedGroups: Set<string>): string | null => {
    for (const group of getItemGroupsById(itemId)) {
      if (ownedGroups.has(group)) return group;
    }
    return null;
  };

  // Helper: track groups of added items
  const addItemGroups = (itemId: string, ownedGroups: Set<string>) => {
    for (const group of getItemGroupsById(itemId)) {
      ownedGroups.add(group);
    }
  };

  // Helper function to build full item order from champion build data
  // Includes core items (3) + boots (1) + supplementary items (2) = 6 total
  // Ensures only 1 pair of boots in the final build
  // Enforces item group restrictions (e.g. can't have Lord Dominik's + Mortal Reminder)
  // For support: forces World Atlas as starter, identifies final support item, skips intermediates
  const buildFullItemOrder = (itemBuild: ChampionBuildData['itemBuild'] | null | undefined, role?: Role): { starter?: string; mainItems: string[] } => {
    if (!itemBuild) return { mainItems: [] };
    
    const isSupport = role === 'sup';
    const isBot = role === 'bot';
    // Bot quest allows 7 items (6 main + boots in 7th slot), other roles get 6
    const maxItems = isBot ? 7 : 6;
    const mainItems: string[] = [];
    const addedIds = new Set<string>();
    const ownedGroups = new Set<string>(); // Track item groups for conflict detection
    let hasBoots = false;
    let hasFinalSupportItem = false;
    
    // Add core items (3)
    const coreIds = [
      itemBuild.coreItems?.item1_id?.toString(),
      itemBuild.coreItems?.item2_id?.toString(),
      itemBuild.coreItems?.item3_id?.toString()
    ].filter(Boolean) as string[];
    
    for (const id of coreIds) {
      if (!addedIds.has(id)) {
        // For support: handle support items specially
        if (isSupport && ALL_SUPPORT_ITEM_IDS.has(id)) {
          if (FINAL_SUPPORT_ITEM_IDS.has(id)) {
            hasFinalSupportItem = true;
            mainItems.push(id);
            addedIds.add(id);
            addItemGroups(id, ownedGroups);
            console.log(`[buildFullItemOrder] 🛡️ Found final support item in core: ${id}`);
          } else {
            console.log(`[buildFullItemOrder] 🛡️ Skipping intermediate support item from core: ${id}`);
          }
          continue;
        }
        // Check item group conflict
        const conflict = hasGroupConflict(id, ownedGroups);
        if (conflict) {
          console.log(`[buildFullItemOrder] ⛔ Skipping core item ${id}: conflicts with group "${conflict}"`);
          continue;
        }
        mainItems.push(id);
        addedIds.add(id);
        addItemGroups(id, ownedGroups);
        if (isBootsItem(id)) hasBoots = true;
      }
    }
    
    // Add boots if available and not already in core
    if (itemBuild.boots?.item_id && !hasBoots) {
      const bootsId = itemBuild.boots.item_id.toString();
      if (!addedIds.has(bootsId)) {
        mainItems.push(bootsId);
        addedIds.add(bootsId);
        addItemGroups(bootsId, ownedGroups);
        hasBoots = true;
      }
    }
    
    // Add supplementary items to fill up to maxItems slots (skip boots if we already have one)
    if (itemBuild.supplementaryItems) {
      for (const item of itemBuild.supplementaryItems) {
        const itemId = item.item_id?.toString();
        if (!itemId) continue;
        if (addedIds.has(itemId)) continue;
        if (isBootsItem(itemId) && hasBoots) continue; // Skip duplicate boots
        // For support: handle support items in supplementary too
        if (isSupport && ALL_SUPPORT_ITEM_IDS.has(itemId)) {
          if (FINAL_SUPPORT_ITEM_IDS.has(itemId) && !hasFinalSupportItem) {
            hasFinalSupportItem = true;
            mainItems.push(itemId);
            addedIds.add(itemId);
            addItemGroups(itemId, ownedGroups);
            console.log(`[buildFullItemOrder] 🛡️ Found final support item in supplementary: ${itemId}`);
          }
          continue;
        }
        
        // Check item group conflict
        const conflict = hasGroupConflict(itemId, ownedGroups);
        if (conflict) {
          console.log(`[buildFullItemOrder] ⛔ Skipping supplementary item ${itemId}: conflicts with group "${conflict}"`);
          continue;
        }
        
        if (mainItems.length < maxItems) {
          mainItems.push(itemId);
          addedIds.add(itemId);
          addItemGroups(itemId, ownedGroups);
          if (isBootsItem(itemId)) hasBoots = true;
        }
      }
    }
    
    // Support: ensure final support item is at position 0
    if (isSupport) {
      const finalIdx = mainItems.findIndex(id => FINAL_SUPPORT_ITEM_IDS.has(id));
      if (finalIdx > 0) {
        // Move existing final support item to position 0
        const [finalItem] = mainItems.splice(finalIdx, 1);
        mainItems.unshift(finalItem);
      } else if (finalIdx === -1) {
        // No final support item found in core/supplementary — use the one from the API response
        const finalSupItemId = itemBuild.finalSupportItem?.item_id?.toString();
        if (finalSupItemId) {
          mainItems.unshift(finalSupItemId);
          console.log(`[buildFullItemOrder] 🛡️ Using final support item from API: ${finalSupItemId} (${itemBuild.finalSupportItem?.name})`);
        }
      }
      // finalIdx === 0 means it's already first, no action needed
    }

    // Determine starter based on role
    const isJungle = role === 'jgl';
    let starterItemId: string | undefined;
    
    if (isSupport) {
      starterItemId = '3865'; // World Atlas
    } else if (isJungle) {
      const dbStarterId = itemBuild.starter?.item_id?.toString();
      starterItemId = (dbStarterId && JUNGLE_STARTER_IDS.has(dbStarterId)) ? dbStarterId : DEFAULT_JUNGLE_STARTER;
    } else {
      const dbStarterId = itemBuild.starter?.item_id?.toString();
      starterItemId = (dbStarterId && JUNGLE_STARTER_IDS.has(dbStarterId)) ? DEFAULT_NON_JUNGLE_STARTER : dbStarterId;
    }

    return {
      starter: starterItemId,
      mainItems: mainItems.slice(0, maxItems) // Bot gets 7 (6 main + boots in 7th slot), others get 6
    };
  };
  
  // Simple wave selection manager
  const waveSelectionManager: WaveSelectionManager = {
    isWaveSelected: (wave: number) => selectedWaves.includes(wave),
    toggleWave: (wave: number) => {
      const isSelected = selectedWaves.includes(wave);
      if (isSelected) {
        setSelectedWaves(prev => prev.filter(w => w !== wave));
        return { added: false, atLimit: false };
      } else {
        // Limit to 10 selected waves
        if (selectedWaves.length >= 10) {
          return { added: false, atLimit: true };
        }
        setSelectedWaves(prev => [...prev, wave].sort((a, b) => a - b));
        // Auto-expand Wave Export when a wave is selected
        setIsWaveExportCollapsed(false);
        return { added: true, atLimit: false };
      }
    },
    getSelectedWaves: () => selectedWaves,
    clearSelection: () => setSelectedWaves([])
  };

  // Create champion objects for MatchupDisplay
  const blueChampion: Champion = {
    id: '1',
    name: championName,
    level: 1,
    teamColor: 'blue'
  };

  const redChampion: Champion | null = opponentName ? {
    id: '2', 
    name: opponentName,
    level: 1,
    teamColor: 'red'
  } : null;

  // Load champion build data from backend
  useEffect(() => {
    // Abort any in-flight request from a previous champion selection
    if (abortControllerRef.current) {
      console.log(`[StaticGridEditor] 🛑 Aborting previous API requests due to champion change`);
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    // Capture the champion names at the time this effect runs
    const effectChampionName = championName;
    const effectOpponentName = opponentName;

    const loadChampionBuilds = async () => {
      try {
        // Clear wave progression state when champion changes
        console.log(`[StaticGridEditor] 🧹 Clearing wave progression state for champion change`);
        setPrimaryWaveProgression([]);
        setOpponentWaveProgression([]);
        setWaveProgressionError(null);
        
        const buildLoadCallId = Math.random().toString(36).substr(2, 6);
        const stack = new Error().stack;
        const callerInfo = stack?.split('\n')[3]?.trim() || 'Unknown caller'; // Go deeper since we're in useEffect
        
        setLoading(true);
        setError(null);

        console.log(`[StaticGridEditor] 🏗️ Call #${buildLoadCallId} - Component ${componentId.current} - Loading builds for ${effectChampionName}${effectOpponentName ? ` vs ${effectOpponentName}` : ''}`);
        console.log(`[StaticGridEditor] 🏗️ Call #${buildLoadCallId} - Called from:`, callerInfo);

        // Prevent duplicate loading
        if (isLoadingBuilds) {
          console.log(`[StaticGridEditor] 🚫 Call #${buildLoadCallId} - Already loading builds, skipping duplicate request`);
          return;
        }
        
        setIsLoadingBuilds(true);

        // Load primary champion build
        const primaryResponse = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${effectChampionName}/item-build`,
          { signal }
        );

        if (!primaryResponse.ok) {
          throw new Error(`Failed to load build for ${effectChampionName}`);
        }

        const primaryBuild = await primaryResponse.json();
        
        // Check if this request was superseded before applying state
        if (signal.aborted) return;
        
        const primaryChampionBuildData = {
          championName: effectChampionName,
          itemBuild: primaryBuild
        };
        setPrimaryChampionBuild(primaryChampionBuildData);

        let opponentChampionBuildData = null;
        // Load opponent champion build if provided
        if (effectOpponentName) {
          const opponentResponse = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${effectOpponentName}/item-build`,
            { signal }
          );

          if (!opponentResponse.ok) {
            console.warn(`Failed to load build for ${effectOpponentName}, continuing without opponent`);
            setOpponentChampionBuild(null);
          } else {
            const opponentBuild = await opponentResponse.json();
            if (signal.aborted) return;
            opponentChampionBuildData = {
              championName: effectOpponentName,
              itemBuild: opponentBuild
            };
            setOpponentChampionBuild(opponentChampionBuildData);
          }
        }

        if (signal.aborted) return;

        console.log(`[StaticGridEditor] Successfully loaded champion builds`);
        console.log(`[StaticGridEditor] Primary build:`, primaryBuild);
        console.log(`[StaticGridEditor] Opponent build:`, effectOpponentName ? opponentChampionBuildData : 'No opponent');
        
        // Load initial wave progression with unified call for both champions
        await loadUnifiedWaveProgressionForBothChampions(primaryChampionBuildData, opponentChampionBuildData, signal);
        
      } catch (err: any) {
        // Don't set error state for aborted requests
        if (err?.name === 'AbortError') {
          console.log(`[StaticGridEditor] ⏹️ Request aborted for ${effectChampionName} vs ${effectOpponentName} (champion changed)`);
          return;
        }
        console.error('[StaticGridEditor] Error loading champion builds:', err);
        setError(err instanceof Error ? err.message : 'Failed to load champion builds');
      } finally {
        if (!signal.aborted) {
          setLoading(false);
          setIsLoadingBuilds(false);
        }
      }
    };

    if (championName) {
      loadChampionBuilds();
    }

    // Cleanup: abort on unmount or next re-run
    return () => {
      abortController.abort();
    };
  }, [championName, opponentName]);
  
  // Debug effect to track when this effect runs
  useEffect(() => {
    console.log(`[StaticGridEditor] 🔄 useEffect triggered with deps:`, {
      championName,
      opponentName,
      isLoadingBuilds,
      loading
    });
  }, [championName, opponentName, isLoadingBuilds, loading]);
  
  // Unified wave progression loading for both champions in a single API call
  const loadUnifiedWaveProgressionForBothChampions = async (
    primaryBuild: ChampionBuildData | null,
    opponentBuild: ChampionBuildData | null,
    signal?: AbortSignal
  ) => {
    try {
      const unifiedCallId = Math.random().toString(36).substr(2, 6);
      const stack = new Error().stack;
      const callerInfo = stack?.split('\n')[2]?.trim() || 'Unknown caller';
      
      setWaveProgressionLoading(true);
      setWaveProgressionError(null);
      
      console.log(`[StaticGridEditor] 🚀 Call #${unifiedCallId} - Component ${componentId.current} - Loading unified wave progression for both champions...`);
      console.log(`[StaticGridEditor] Call #${unifiedCallId} - Called from:`, callerInfo);
      console.log(`[StaticGridEditor] Call #${unifiedCallId} - Primary: ${championName}`, primaryBuild);
      console.log(`[StaticGridEditor] Call #${unifiedCallId} - Opponent: ${opponentName || 'Garen'}`, opponentBuild);
      
      // Default gold events (CSPM depends on role: 0 for support, 10 otherwise)
      const defaultGoldEvents: GoldEventsData = {
        cspm: getDefaultCSPM(primaryRole),
        cspmRanges: [],
        kills: [],
        turrets: [],
        plates: [],
        miscellaneous: [],
        supportProcsPerMin: getDefaultSupportProcs(primaryRole)
      };
      
      // Get build orders for both champions (include all items: core + boots + supplementary)
      const primaryBuildOrder = buildFullItemOrder(primaryBuild?.itemBuild, primaryRole);
      
      console.log(`[StaticGridEditor] 📦 Primary build order:`, primaryBuildOrder);
      console.log(`[StaticGridEditor] 💰 Gold events:`, defaultGoldEvents);
      
      // Single unified API call - let backend handle both champions and auto-creation
      console.log(`[StaticGridEditor] 🌐 Making unified API call...`);
      const result = await updateGoldEventsInBackendWithBuildOrder(
        championName, 
        defaultGoldEvents, 
        primaryBuildOrder, 
        undefined, // no auto-export waves
        opponentName || 'Garen', // opponent name
        true, // enable auto-creation of champions in backend
        primaryRole, // pass the current role
        opponentRole, // pass the opponent role for quest calculations
        signal
      );
      
      console.log(`[StaticGridEditor] 📨 Unified API Response:`, result);
      console.log(`[StaticGridEditor] 🔍 Response structure:`, {
        success: result.success,
        hasData: !!result.data,
        dataKeys: result.data ? Object.keys(result.data) : [],
        hasPrimaryWaves: !!result.data?.waveProgression,
        hasOpponentWaves: !!result.data?.opponentWaves,
        hasChampionGrid: !!result.data?.championGrid
      });
      
      // Don't apply state if this request was superseded by a newer champion selection
      if (signal?.aborted) {
        console.log(`[StaticGridEditor] ⏹️ Skipping state update — request was aborted`);
        return;
      }
      
      if (result.success && result.data) {
        // Set primary wave progression
        if (result.data.waveProgression) {
          setPrimaryWaveProgression(result.data.waveProgression);
          console.log(`[StaticGridEditor] ✅ Set primary wave progression: ${result.data.waveProgression.length} waves`);
        }
        
        // Set opponent wave progression (if available)
        if (result.data.opponentWaves) {
          setOpponentWaveProgression(result.data.opponentWaves);
          console.log(`[StaticGridEditor] ✅ Set opponent wave progression: ${result.data.opponentWaves.length} waves`);
        }
        
        // Apply backend-provided effective build orders (source of truth for group conflict filtering)
        const newEffectiveBuilds: Record<string, { starter?: string; mainItems: string[] }> = {};
        if (result.data.effectiveBuildOrder) {
          newEffectiveBuilds[championName] = result.data.effectiveBuildOrder;
          console.log(`[StaticGridEditor] ✅ Got effective build order for ${championName}: ${result.data.effectiveBuildOrder.mainItems.length} items`);
        }
        if (result.data.opponentEffectiveBuildOrder && opponentName) {
          newEffectiveBuilds[opponentName] = result.data.opponentEffectiveBuildOrder;
          console.log(`[StaticGridEditor] ✅ Got effective build order for ${opponentName}: ${result.data.opponentEffectiveBuildOrder.mainItems.length} items`);
        }
        if (Object.keys(newEffectiveBuilds).length > 0) {
          setEffectiveBuildOrders(newEffectiveBuilds);
        }
        
            // Handle auto-created champion grid from backend
            if (result.data.championGrid && onWaveExportResult) {
              console.log(`[StaticGridEditor] 🎯 Auto-created champion grid received from backend:`, result.data.championGrid);
              
              // Create proper WaveAnalysisResponse structure
              const waveAnalysisResponse = {
                success: true,
                data: result.data.championGrid // championGrid already has champions and metadata
              };
              
              console.log(`[StaticGridEditor] 📤 Sending wave analysis response to parent:`, waveAnalysisResponse);
              onWaveExportResult(waveAnalysisResponse, null);
            }
        
        console.log(`[StaticGridEditor] 🎉 Unified loading complete!`);
      } else {
        console.error(`[StaticGridEditor] ❌ Unified loading failed: ${result.error || 'No error message'}`);
        setWaveProgressionError(result.error || 'Failed to load wave progression');
      }
      
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.log(`[StaticGridEditor] ⏹️ Unified wave progression request aborted (champion changed)`);
        return;
      }
      console.error(`[StaticGridEditor] ❌ Error in unified wave progression loading:`, error);
      setWaveProgressionError('Failed to load wave progression');
    } finally {
      if (!signal?.aborted) {
        setWaveProgressionLoading(false);
      }
    }
  };
  
  // LEGACY: Load wave progression for a specific champion (replaced by unified approach above)
  const loadWaveProgressionForChampion = async (
    championName: string, 
    championType: 'primary' | 'opponent',
    championBuild: ChampionBuildData | null
  ) => {
    try {
      console.log(`[StaticGridEditor] 🔄 Starting wave progression for ${championName} (${championType})`);
      console.log(`[StaticGridEditor] Champion build data:`, championBuild);
      
      // Default gold events (CSPM depends on role: 0 for support, 10 otherwise)
      const roleForChamp = championType === 'primary' ? primaryRole : opponentRole;
      const defaultGoldEvents: GoldEventsData = {
        cspm: getDefaultCSPM(roleForChamp),
        cspmRanges: [],
        kills: [],
        turrets: [],
        plates: [],
        miscellaneous: [],
        supportProcsPerMin: getDefaultSupportProcs(roleForChamp)
      };
      
      // Get build order from champion build data (full 6-item build)
      const buildOrder = buildFullItemOrder(championBuild?.itemBuild, roleForChamp);
      
      console.log(`[StaticGridEditor] 📦 Build order for ${championName}:`, buildOrder);
      console.log(`[StaticGridEditor] 💰 Gold events:`, defaultGoldEvents);
      
      // Call backend API with specific build order (auto-export disabled for now)
      console.log(`[StaticGridEditor] 🌐 Calling backend API for ${championName}...`);
      // const shouldAutoExport = championType === 'primary'; // Only auto-export for primary champion
      // Note: Wave range is now determined by backend configuration
      // const opponentName = championType === 'primary' ? (opponentChampionBuild?.championName || 'Garen') : undefined;
      // Only allow champion creation for the primary champion during initialization
      const shouldCreateChampions = championType === 'primary';
      const result = await updateGoldEventsInBackendWithBuildOrder(championName, defaultGoldEvents, buildOrder, undefined, undefined, shouldCreateChampions);
      
      console.log(`[StaticGridEditor] 📨 API Response for ${championName}:`, result);
      console.log(`[StaticGridEditor] 🔍 Response data structure:`, {
        success: result.success,
        hasData: !!result.data,
        dataKeys: result.data ? Object.keys(result.data) : [],
        hasWaveProgression: !!result.data?.waveProgression,
        waveProgressionLength: result.data?.waveProgression?.length,
        dataStructure: result.data
      });
      
      if (result.success && result.data?.waveProgression) {
        if (championType === 'primary') {
          setPrimaryWaveProgression(result.data.waveProgression);
          console.log(`[StaticGridEditor] 🎯 Set primary wave progression: ${result.data.waveProgression.length} waves`);
        } else {
          setOpponentWaveProgression(result.data.waveProgression);
          console.log(`[StaticGridEditor] 🎯 Set opponent wave progression: ${result.data.waveProgression.length} waves`);
        }
        console.log(`[StaticGridEditor] ✅ Loaded ${result.data.waveProgression.length} default waves for ${championName} (${championType})`);
        
        // Handle auto-created champion grid from backend (only for primary champion to avoid duplicates)
        if (championType === 'primary' && result.data.championGrid && onWaveExportResult) {
          console.log(`[StaticGridEditor] 🎯 Auto-created champion grid received from backend:`, result.data.championGrid);
          onWaveExportResult(result.data, null);
        }
      } else {
        console.error(`[StaticGridEditor] ❌ Failed to load wave progression for ${championName}: ${result.error || 'No error message'}`);
        console.error(`[StaticGridEditor] ❌ Full result:`, result);
      }
      
    } catch (error) {
      console.error(`[StaticGridEditor] ❌ Error loading wave progression for ${championName}:`, error);
      throw error;
    }
  };

  // API call function for updating build orders
  const updateBuildOrderInBackend = async (
    championName: string, 
    buildOrder: { starter?: string; mainItems: string[] },
    role?: 'top' | 'jgl' | 'mid' | 'bot' | 'sup'
  ): Promise<BuildOrderUpdateResponse> => {
    // Get wave config for maxWaves value
    const waveConfig = await getWaveConfig();
    
    const requestData: BuildOrderUpdateRequest = {
      championName,
      buildOrder,
      role,
      maxWaves: waveConfig.maxWaves,
      goldScenario: 'even',
      cspm: getDefaultCSPM(role || primaryRole)
    };

    console.log(`[StaticGridEditor] Sending build order update to backend:`, requestData);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/wave/build-order/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: BuildOrderUpdateResponse = await response.json();
    console.log(`[StaticGridEditor] Backend response:`, result);
    
    return result;
  };

  // Handle build order changes (calls backend API)
  const handleBuildOrderChange = async (championName: string, buildOrder: { starter?: string; mainItems: string[] }) => {
    console.log(`[StaticGridEditor] Build order changed for ${championName}:`, buildOrder);
    
    // Determine the role for this champion
    const role = championName === (opponentName || 'Garen') ? opponentRole : primaryRole;
    const isPrimaryChampion = championName !== (opponentName || 'Garen');
    
    // Save the custom build order to state so it persists for gold events updates
    if (isPrimaryChampion) {
      console.log(`[StaticGridEditor] 💾 Saving PRIMARY custom build order:`, buildOrder);
      setPrimaryCustomBuildOrder(buildOrder);
    } else {
      console.log(`[StaticGridEditor] 💾 Saving OPPONENT custom build order:`, buildOrder);
      setOpponentCustomBuildOrder(buildOrder);
    }
    
    try {
      // Call backend API to update build order and get new wave progression
      const result = await updateBuildOrderInBackend(championName, buildOrder, role);
      
      if (result.success && result.data) {
        console.log(`[StaticGridEditor] ✅ Build order updated successfully for ${championName}`);
        console.log(`[StaticGridEditor] New wave progression has ${result.data.totalWaves} waves`);
        
        // Update wave progression state with the new data
        if (result.data.waveProgression && result.data.waveProgression.length > 0) {
          if (isPrimaryChampion) {
            console.log(`[StaticGridEditor] 📊 Updating PRIMARY wave progression with ${result.data.waveProgression.length} waves`);
            setPrimaryWaveProgression(result.data.waveProgression);
          } else {
            console.log(`[StaticGridEditor] 📊 Updating OPPONENT wave progression with ${result.data.waveProgression.length} waves`);
            setOpponentWaveProgression(result.data.waveProgression);
          }
        }
        
        // Apply backend-filtered effective build order (source of truth)
        const effectiveBuild = result.data?.effectiveBuildOrder;
        if (effectiveBuild) {
          setEffectiveBuildOrders(prev => ({
            ...prev,
            [championName]: effectiveBuild
          }));
        }
        
        // Call parent callback with the result
        if (onBuildOrderChange) {
          onBuildOrderChange(championName, buildOrder);
        }
      } else {
        console.error(`[StaticGridEditor] ❌ Failed to update build order: ${result.error}`);
      }
      
    } catch (error) {
      console.error(`[StaticGridEditor] ❌ Error calling backend API:`, error);
    }
  };

  // Handle item clicks (for future expansion)
  const handleItemClick = (itemId: number, itemName: string) => {
    console.log(`[StaticGridEditor] Item clicked: ${itemName} (${itemId})`);
    // Future: Could open item details modal or trigger other actions
  };

  // API call function for updating gold events with specific build order
  const updateGoldEventsInBackendWithBuildOrder = async (
    championName: string, 
    goldEvents: GoldEventsData, 
    buildOrder: { starter?: string; mainItems: string[] },
    autoExportWaves?: number[],
    opponentNameOverride?: string,
    autoCreateChampions?: boolean,
    role?: 'top' | 'jgl' | 'mid' | 'bot' | 'sup',
    opponentRoleParam?: 'top' | 'jgl' | 'mid' | 'bot' | 'sup',
    signal?: AbortSignal
  ): Promise<any> => {
    // Use the prop opponentName first, then override, then fallback to 'Garen'
    const finalOpponentName = opponentNameOverride || opponentName || 'Garen';
    
    // Generate unique call ID to track this request
    const callId = Math.random().toString(36).substr(2, 6);
    const stack = new Error().stack;
    const callerInfo = stack?.split('\n')[2]?.trim() || 'Unknown caller';
    
    console.log(`[StaticGridEditor] 🎯 API CALL PARAMETERS (Call #${callId}):`, {
      championName: `"${championName}"`,
      opponentName: `"${finalOpponentName}"`,
      buildOrder,
      role,
      goldEvents: {
        cspm: goldEvents.cspm,
        eventCounts: {
          kills: goldEvents.kills.length,
          turrets: goldEvents.turrets.length,
          plates: goldEvents.plates.length,
          misc: goldEvents.miscellaneous.length
        }
      },
      autoCreateChampions,
      caller: callerInfo
    });
    
    // Get wave config for maxWaves value
    const waveConfig = await getWaveConfig();
    
    const requestData: {
      championName: string;
      goldEvents: GoldEventsData;
      buildOrder: { starter?: string; mainItems: string[] };
      role?: 'top' | 'jgl' | 'mid' | 'bot' | 'sup';
      opponentRole?: 'top' | 'jgl' | 'mid' | 'bot' | 'sup';
      maxWaves: number;
      opponentName: string;
      autoExportWaves?: number[];
      autoCreateChampions?: boolean;
    } = {
      championName,
      goldEvents,
      buildOrder,
      role,
      opponentRole: opponentRoleParam,
      maxWaves: waveConfig.maxWaves,
      opponentName: finalOpponentName,
      autoCreateChampions: autoCreateChampions
    };

    // Add auto-export parameters if provided (legacy support)
    if (autoExportWaves && autoExportWaves.length > 0) {
      requestData.autoExportWaves = autoExportWaves;
    }

    console.log(`[StaticGridEditor] 📤 Call #${callId} - Sending gold events update to backend:`, requestData);

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    };
    if (signal) fetchOptions.signal = signal;

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/wave/gold-events/update`, fetchOptions);

    console.log(`[StaticGridEditor] 📡 Call #${callId} - Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[StaticGridEditor] ❌ HTTP error! status: ${response.status}, body:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const result = await response.json();
    console.log(`[StaticGridEditor] 📥 Backend response:`, result);
    
    return result;
  };

  // API call function for updating gold events (uses current active champion's build)
  const updateGoldEventsInBackend = async (championName: string, goldEvents: GoldEventsData): Promise<any> => {
    // Get wave config for maxWaves value
    const waveConfig = await getWaveConfig();
    
    // Determine role for the active champion
    const role = activeChampion === 'primary' ? primaryRole : opponentRole;
    
    // Use custom build order if set (from ItemShop changes), otherwise fall back to default
    const customBuildOrder = activeChampion === 'primary' ? primaryCustomBuildOrder : opponentCustomBuildOrder;
    const activeBuildOrder = customBuildOrder || (activeChampion === 'primary' ? 
      buildFullItemOrder(primaryChampionBuild?.itemBuild, primaryRole) :
      buildFullItemOrder(opponentChampionBuild?.itemBuild, opponentRole));
    
    const requestData = {
      championName,
      goldEvents,
      buildOrder: activeBuildOrder,
      role,
      maxWaves: waveConfig.maxWaves
    };

    console.log(`[StaticGridEditor] Sending gold events update to backend:`, requestData);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/wave/gold-events/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log(`[StaticGridEditor] Backend response:`, result);
    
    return result;
  };

  // Handle gold events changes
  const handleGoldEventsChange = async (goldEvents: GoldEventsData) => {
    console.log(`[StaticGridEditor] Gold events changed for ${activeChampion}:`, goldEvents);
    
    if (activeChampion === 'primary') {
      setPrimaryGoldEvents(goldEvents);
    } else {
      setOpponentGoldEvents(goldEvents);
    }
    
    try {
      // Call backend API to update gold events and recalculate wave progression
      const activeChampionName = activeChampion === 'primary' ? championName : opponentName;
      if (activeChampionName) {
        // Get the build order for the active champion - use custom build order if set, otherwise fall back to default
        const customBuildOrder = activeChampion === 'primary' ? primaryCustomBuildOrder : opponentCustomBuildOrder;
        
        let activeBuildOrder: { starter?: string; mainItems: string[] };
        
        if (customBuildOrder) {
          // Use the custom build order that was set via ItemShop
          console.log(`[StaticGridEditor] 📦 Using CUSTOM build order for ${activeChampionName}:`, customBuildOrder);
          activeBuildOrder = customBuildOrder;
        } else {
          // Fall back to default build from champion data
          console.log(`[StaticGridEditor] 📦 Using DEFAULT build order for ${activeChampionName}`);
          activeBuildOrder = activeChampion === 'primary' ? 
            buildFullItemOrder(primaryChampionBuild?.itemBuild, primaryRole) :
            buildFullItemOrder(opponentChampionBuild?.itemBuild, opponentRole);
        }
          
        // Subsequent call during gold events change - disable champion creation to prevent duplicates
        const activeRole = activeChampion === 'primary' ? primaryRole : opponentRole;
        const inactiveRole = activeChampion === 'primary' ? opponentRole : primaryRole;
        const result = await updateGoldEventsInBackendWithBuildOrder(activeChampionName, goldEvents, activeBuildOrder, undefined, undefined, false, activeRole, inactiveRole);
        
        if (result.success && result.data) {
          console.log(`[StaticGridEditor] ✅ Gold events updated successfully for ${activeChampionName}`);
          console.log(`[StaticGridEditor] New wave progression has ${result.data.totalWaves} waves`);
          
          // Update wave progression data
          if (result.data.waveProgression) {
            if (activeChampion === 'primary') {
              setPrimaryWaveProgression(result.data.waveProgression);
            } else {
              setOpponentWaveProgression(result.data.waveProgression);
            }
          }
          
          // Apply backend-filtered effective build order (source of truth)
          const effectiveBuild = result.data?.effectiveBuildOrder;
          if (effectiveBuild) {
            setEffectiveBuildOrders(prev => ({
              ...prev,
              [activeChampionName]: effectiveBuild
            }));
          }
          
          // Call parent callback with the result
          if (onGoldEventsChange) {
            onGoldEventsChange(activeChampionName, goldEvents);
          }
        } else {
          console.error(`[StaticGridEditor] ❌ Failed to update gold events: ${result.error}`);
        }
      }
      
    } catch (error) {
      console.error(`[StaticGridEditor] ❌ Error calling backend API:`, error);
    }
  };

  // Handle active champion change
  const handleActiveChampionChange = (champion: 'primary' | 'opponent') => {
    setActiveChampion(champion);
  };
  
  // Handle active wave champion change
  const handleActiveWaveChampionChange = (champion: 'primary' | 'opponent') => {
    setActiveWaveChampion(champion);
  };
  
  // Handle wave export
  const handleWaveExport = (selectedWaves: number[]) => {
    console.log(`[StaticGridEditor] Exporting waves:`, selectedWaves);
    if (onWaveExport) {
      onWaveExport(selectedWaves, activeWaveChampion === 'primary' ? championName : opponentName || '');
    }
  };

  // Handle wave export to backend - sends wave numbers with current gold events and build order
  const handleWaveExportToBackend = async (selectedWaves: number[], groupLabel?: string) => {
    console.log('[StaticGridEditor] 🚀 Exporting waves to backend:', selectedWaves, 'Group:', groupLabel);
    
    try {
      // Get current gold events for the active champion
      const currentGoldEvents = activeChampion === 'primary' ? primaryGoldEvents : opponentGoldEvents;
      
      // Get current build order - use custom build order if set (from ItemShop changes),
      // otherwise fall back to default build from champion data
      const customBuildOrder = activeChampion === 'primary' ? primaryCustomBuildOrder : opponentCustomBuildOrder;
      let currentBuildOrder: { starter?: string; mainItems: string[] };
      
      if (customBuildOrder) {
        console.log('[StaticGridEditor] 📦 Using CUSTOM build order for export:', customBuildOrder);
        currentBuildOrder = customBuildOrder;
      } else {
        const currentBuild = activeChampion === 'primary' ? primaryChampionBuild : opponentChampionBuild;
        const currentRole = activeChampion === 'primary' ? primaryRole : opponentRole;
        currentBuildOrder = buildFullItemOrder(currentBuild?.itemBuild, currentRole);
        console.log('[StaticGridEditor] 📦 Using DEFAULT build order for export:', currentBuildOrder);
      }

      console.log('[StaticGridEditor] 💰 Using gold events for export:', currentGoldEvents);
      
      // Enhanced API call - send wave numbers with current context
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/wave/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          opponentName: opponentName,
          waveNumbers: selectedWaves,
          groupLabel: groupLabel || 'Custom Export',
          goldEvents: currentGoldEvents,
          buildOrder: currentBuildOrder,
          role: primaryRole,
          opponentRole: opponentRole
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('[StaticGridEditor] ✅ Wave export successful:', result.data);
        // Call the callback to update WaveAnalysisGrid
        if (onWaveExportResult) {
          onWaveExportResult(result, null);
        }
      } else {
        console.error('[StaticGridEditor] ❌ Wave export failed:', result.error);
        if (onWaveExportResult) {
          onWaveExportResult(null, result.error || 'Export failed');
        }
      }
      
    } catch (error) {
      console.error('[StaticGridEditor] 💥 Wave export error:', error);
      if (onWaveExportResult) {
        onWaveExportResult(null, error instanceof Error ? error.message : 'Unknown error');
      }
    }
  };
  
  // Get item image URL using Data Dragon CDN (same as ItemBuildProgressionV2)
  const [apiVersion, setApiVersion] = useState<string>("15.9.1");
  
  // Fetch the latest API version when component mounts
  useEffect(() => {
    const fetchApiVersion = async () => {
      try {
        const response = await fetch('https://ddragon.leagueoflegends.com/api/versions.json');
        if (response.ok) {
          const versions = await response.json();
          if (versions && versions.length > 0) {
            setApiVersion(versions[0]);
            console.log(`[StaticGridEditor] Using Data Dragon version: ${versions[0]}`);
          }
        }
      } catch (err) {
        console.warn('[StaticGridEditor] Error fetching API version, using default:', err);
      }
    };
    
    fetchApiVersion();
  }, []);

  const getItemImageUrl = (itemId: number): string => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };

  return (
    <div className={`ge-grid-editor ${className}`}>
      {/* Header */}
      <div className="ge-grid-editor-header">
        <h3>
          Matchup Analysis
          <span
            className="ge-info-icon"
            title="Show description"
            onClick={() => setIsDescriptionVisible(v => !v)}
          >ⓘ</span>
        </h3>
        {isDescriptionVisible && (
          <div className="ge-description-dropdown">
            <p>
              This tool analyzes champion performance by simulating gold income and item progression throughout a match.
              The analysis follows a complete workflow that connects each section:
            </p>
            <p>
              <strong>Default Setup:</strong> Both champions start with their most popular item builds from real game data.
              The default gold income is set to <span className="ge-description-highlight">10 CSPM</span> (creep score per minute) with no additional events.
            </p>
            <p>
              <strong>Customizable Progression:</strong> You can adjust gold settings by changing CSPM rates or adding specific events like kills, turrets, and plates at chosen waves.
              This determines when and which items can be purchased.
            </p>
            <p>
              <strong>Wave-by-Wave Analysis:</strong> The progression table shows exactly when each champion would acquire items based on your gold settings,
              tracking their power growth wave by wave.
            </p>
            <p>
              <strong>Damage Comparison:</strong> The final analysis grid compares both champions using their runes, skill order, and items at key moments —
              early levels (1-6) and major item power spikes (1-6 items). You can customize any element to experiment with different scenarios.
            </p>
            <p>
              All sections work together to show how matchup dynamics shift as champions gain gold and items throughout the game.
            </p>
          </div>
        )}
      </div>
      
      <div className="ge-grid-editor-content">
        
        {/* Matchup Display - Clean design from GridEditor */}
        <MatchupDisplay 
          blueChampion={blueChampion}
          redChampion={redChampion}
          // No onClick handler - this is display-only now
        />

        {/* Item Build Progression - Backend Optimized */}
        <ItemBuildProgressionV2
          primaryChampion={primaryChampionBuild || { championName, itemBuild: null }}
          opponentChampion={opponentChampionBuild || undefined}
          showBothChampions={!!opponentName}
          loading={loading}
          error={error}
          onBuildOrderChange={handleBuildOrderChange}
          onItemClick={handleItemClick}
          primaryRole={primaryRole}
          opponentRole={opponentRole}
          onPrimaryRoleChange={setPrimaryRole}
          onOpponentRoleChange={setOpponentRole}
          effectiveBuildOrders={effectiveBuildOrders}
        />

        {/* Gold & Wave Progression Section */}
        <div className="ge-customize-section">
          <div className="ge-customize-header" onClick={() => setIsCustomizeCollapsed(!isCustomizeCollapsed)}>
            <span className="ge-collapse-arrow">{isCustomizeCollapsed ? '▶' : '▼'}</span>
            <h3>Gold Settings &amp; Wave Progression</h3>
          </div>

        {!isCustomizeCollapsed && (<div className="ge-customize-body">
        {/* Gold Settings */}
        <div className="ge-enhanced-gold-controls-container">
          <div className="ge-section-header" onClick={() => setIsGoldControlsCollapsed(!isGoldControlsCollapsed)}>
            <span className="ge-collapse-arrow">{isGoldControlsCollapsed ? '▶' : '▼'}</span>
            <h4>Gold Settings</h4>
          </div>
          {!isGoldControlsCollapsed && (
            <div className="ge-section-description">
              <p>Set gold income rates and events that determine when items can be purchased</p>
              <p>Default: 10 CSPM with no events. Add kills, turrets, plates, or adjust CSPM for different scenarios</p>
            </div>
          )}
          {!isGoldControlsCollapsed && (
            <GoldControlsV2
              goldEvents={activeChampion === 'primary' ? primaryGoldEvents : opponentGoldEvents}
              onChange={handleGoldEventsChange}
              primaryGoldEvents={primaryGoldEvents}
              opponentGoldEvents={opponentGoldEvents}
              maxWave={120}
              primaryChampionName={championName}
              opponentChampionName={opponentName || 'Opponent'}
              activeChampion={activeChampion}
              onActiveChampionChange={handleActiveChampionChange}
              maxWaveLimit={120}
              primaryWaveProgression={primaryWaveProgression}
              opponentWaveProgression={opponentWaveProgression}
              primaryRole={primaryRole}
              opponentRole={opponentRole}
              onPrimaryRoleChange={setPrimaryRole}
              onOpponentRoleChange={setOpponentRole}
              onQuestCompletionChange={(pWave, oWave) => {
                setPrimaryQuestWave(pWave);
                setOpponentQuestWave(oWave);
              }}
            />
          )}
        </div>

        {/* Wave Progression Table */}
        <div className="ge-wave-progression-table-container">
          <div className="ge-section-header" onClick={() => setIsWaveTableCollapsed(!isWaveTableCollapsed)}>
            <span className="ge-collapse-arrow">{isWaveTableCollapsed ? '▶' : '▼'}</span>
            <h4>Wave Progression Table</h4>
          </div>
          {!isWaveTableCollapsed && (
            <div className="ge-section-description">
              <p>See exactly when each champion acquires items based on your gold settings</p>
              <p>Track power progression wave-by-wave and select specific moments for detailed analysis</p>
            </div>
          )}
          {!isWaveTableCollapsed && (
            <WaveProgressionTableV3
              primaryWaves={primaryWaveProgression}
              opponentWaves={opponentWaveProgression}
              primaryChampionName={championName}
              opponentChampionName={opponentName}
              activeWaveChampion={activeWaveChampion}
              onActiveWaveChampionChange={handleActiveWaveChampionChange}
              primaryGoldEvents={[
                ...primaryGoldEvents.kills.map(event => ({ ...event, type: 'kill' })),
                ...primaryGoldEvents.turrets.map(event => ({ ...event, type: 'turret' })),
                ...primaryGoldEvents.plates.map(event => ({ ...event, type: 'plate' })),
                ...primaryGoldEvents.miscellaneous.map(event => ({ ...event, type: 'misc' }))
              ] as any[]}
              opponentGoldEvents={[
                ...opponentGoldEvents.kills.map(event => ({ ...event, type: 'kill' })),
                ...opponentGoldEvents.turrets.map(event => ({ ...event, type: 'turret' })),
                ...opponentGoldEvents.plates.map(event => ({ ...event, type: 'plate' })),
                ...opponentGoldEvents.miscellaneous.map(event => ({ ...event, type: 'misc' }))
              ] as any[]}
              selectionManager={waveSelectionManager}
              existingGridWaves={[]}
              onWaveExport={handleWaveExport}
              getItemImageUrl={getItemImageUrl}
              primaryQuestCompletionWave={primaryQuestWave}
              opponentQuestCompletionWave={opponentQuestWave}
              loading={waveProgressionLoading}
              error={waveProgressionError || undefined}
            />
          )}

          {/* Wave Export - sub-section of Wave Progression Table */}
          {!isWaveTableCollapsed && (
            <div className="ge-wave-export-subsection">
              <div className="ge-subsection-header" onClick={() => setIsWaveExportCollapsed(!isWaveExportCollapsed)}>
                <span className="ge-collapse-arrow">{isWaveExportCollapsed ? '▶' : '▼'}</span>
                <span className="ge-subsection-title">Wave Export</span>
              </div>
              {!isWaveExportCollapsed && (
                <div className="ge-section-description">
                  <p>Export selected waves to the damage analysis grid for direct comparison</p>
                  <p>Choose key moments like item completions or level breakpoints for matchup analysis</p>
                </div>
              )}
              {!isWaveExportCollapsed && (
                <WaveExportV4
                  primaryWaves={primaryWaveProgression}
                  opponentWaves={opponentWaveProgression}
                  primaryChampionName={championName}
                  opponentChampionName={opponentName}
                  primaryChampionData={primaryChampionBuild}
                  opponentChampionData={opponentChampionBuild}
                  selectionManager={waveSelectionManager}
                  getItemImageUrl={getItemImageUrl}
                  onWaveExport={handleWaveExportToBackend}
                  error={waveProgressionError || undefined}
                />  
              )}
            </div>
          )}
        </div>

        </div>)}
        </div> {/* End of ge-customize-section */}

        {/* Debug Section - Temporary for debugging wave progression */}
       {/*  <div className="ge-debug-container" style={{ 
          background: '#f0f0f0', 
          padding: '15px', 
          margin: '10px 0', 
          borderRadius: '8px',
          border: '2px solid #ddd'
        }}>
          <div className="ge-section-header">
            <h4>🔧 Debug Wave Progression</h4>
          </div>
          <div className="ge-debug-info">
            <p><strong>Primary Waves:</strong> {primaryWaveProgression.length} loaded</p>
            <p><strong>Opponent Waves:</strong> {opponentWaveProgression.length} loaded</p>
            <p><strong>Loading:</strong> {waveProgressionLoading ? 'Yes' : 'No'}</p>
            <p><strong>Error:</strong> {waveProgressionError || 'None'}</p>
          </div>
          <div className="ge-debug-buttons">
            <button 
              onClick={() => loadDefaultWaveProgressionWithBuilds(primaryChampionBuild, opponentChampionBuild)}
              disabled={waveProgressionLoading}
              style={{ marginRight: '10px', padding: '8px 16px' }}
            >
              {waveProgressionLoading ? 'Loading...' : 'Reload Wave Data'}
            </button>
            <button 
              onClick={() => {
                console.log('=== CURRENT STATE DEBUG ===');
                console.log('Primary Champion Build:', primaryChampionBuild);
                console.log('Opponent Champion Build:', opponentChampionBuild);
                console.log('Primary Wave Progression:', primaryWaveProgression);
                console.log('Opponent Wave Progression:', opponentWaveProgression);
                console.log('Active Wave Champion:', activeWaveChampion);
                console.log('Loading:', waveProgressionLoading);
                console.log('Error:', waveProgressionError);
                console.log('=== END DEBUG ===');
              }}
              style={{ padding: '8px 16px' }}
            >
              Log Current State
            </button>
          </div>
        </div> */}

     

      </div>
    </div>
  );
};

export default StaticGridEditor;
